import React, { useState } from 'react';
import { Alert, Container, Image } from 'react-bootstrap';
import './notification.css';
import rawNotificationData from './notification.json';

type Link = {
        text: string;
        href: string;
};

interface NotificationData{
    message: string[];
    links:{ [key: string]: Link };
    highlights: { [key:string]: string };
}

type Token =
| {type:'text'; content:string }
| {type:'bold'; content:string }
| {type:'italic'; content:string }
| {type:'bold-italic'; content:string }
| {type:'underline'; content:string }
| {type:'strikethrough'; content:string}
| {type:'link'; key:string }
| {type:'highlight'; key:string };

const notificationData = rawNotificationData as NotificationData;

const TokeniseLine = (line:string): Token[] => {
    const pattern = /({{.*?}}|\*\*\*.*?\*\*\*|\*\*.*?\*\*|\*.*?\*|`.*?`|_.*?_|~.*?~)/g;
    const parts = line.split(pattern);
    const tokens:Token[] = [];

    for(const part of parts) {
        if(!part) continue;

        if(/^\*\*\*(.*?)\*\*\*$/.test(part))
            tokens.push({type:'bold-italic', content: part.slice(3, -3)});
        else if(/^\*\*(.*?)\*\*$/.test(part))
            tokens.push({type:'bold', content:part.slice(2, -2)});
        else if(/^\*(.*?)\*$/.test(part))
            tokens.push({type:'italic', content:part.slice(1, -1)});
        else if(/^_(.*?)_$/.test(part))
            tokens.push({type:'underline', content:part.slice(1, -1)});
        else if(/^~(.*?)~$/.test(part))
            tokens.push({type:'strikethrough', content:part.slice(1, -1)});
        else if(/^{{(.*?)}}$/.test(part))
            tokens.push({type:'link', key:part.slice(2, -2)});
        else if(/^`(.*?)`$/.test(part))
            tokens.push({type:'highlight', key:part.slice(1, -1)});
        else
            tokens.push({type:'text', content:part});

    }
    return tokens;
};

const RenderTokens = (tokens: Token[], links: NotificationData["links"], highlights:NotificationData["highlights"]) : React.ReactElement[] => {
    return tokens.map((token, index) => {
        switch(token.type) {
            case 'text':
                return <span key={index}>{token.content}</span>;
            case 'italic':
                return <em key={index}>{token.content}</em>;
            case 'bold':
                return <strong key={index}>{token.content}</strong>;
            case 'bold-italic':
                return <strong key={index}><em>{token.content}</em></strong>;
            case 'strikethrough':
                return <span key={index} style={{ textDecoration: 'line-through' }}>{token.content}</span>;
            case 'underline':
                return <span key={index} style={{ textDecoration: 'underline' }}>{token.content}</span>;
            case 'link':
                const link = links[token.key];
                return link ? (
                    <a
                        key={index}
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {link.text}
                    </a>
                ) : null;
            case 'highlight':
                const color = highlights[token.key];
                return <span key={index} style={{ color }}>{token.key}</span>;
            default:
                return null;
        }
    }).filter(Boolean) as React.ReactElement[];
};

export const NotificationBanner = () => {
    const [show, setShow] = useState(true);
    const [expanded, setExpanded] = useState(false);

    const renderedLines = notificationData.message.map((line, index) => {
        if(line.trim() === "")
            return <div key={index} style={{ height: '1em' }}>&nbsp;</div>;

        const tokens = TokeniseLine(line);
        return (
            <div key={index}>
                {RenderTokens(tokens, notificationData.links, notificationData.highlights)}
            </div>
        );
    });

    if(!show) return null;

    return (
        <div className="notification-zone">
            <Container fluid className="yellow-zone">
                <Alert
                    variant="warning"
                    className="d-flex align-items-center p-3"
                    onClose={() => setShow(false)}
                    dismissible
                    style={{ backgroundColor: '#fff8e1', border: 'none', marginBottom: '0' }}
                >
                    {/* Icon */}
                    <div className="d-flex align-items-center me-3">
                        <Image
                            src="/ClientApp/build/Images/Icons/icon-notification-warning.svg"
                            roundedCircle
                            alt="Warning Icon"
                            width={30}
                            height={30}
                        />
                    </div>

                    {/* Notification Text */}
                    <div className="notification-body">
                        <div
                            onClick={() => setExpanded((prev) => !prev)}
                            className={`notification-text ${expanded ? 'more' : 'less'}`}
                        >
                            <blockquote className="mb-0">{renderedLines}</blockquote>
                        </div>
                    </div>

                    {/* Close Button */}
                    <div className="d-flex align-items-center ms-3">
                        <button
                            type="button"
                            className="btn-close"
                            aria-label="Close"
                            onClick={() => setShow(false)}
                        />
                    </div>
                </Alert>
            </Container>
        </div>


    );
};

//export default NotificationBanner;